import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { Aviso, Campo } from "../componentes/piezas";
import { SelectorIdioma, useIdioma, type Idioma } from "../lib/idioma";
import { CLAVE_CLUB, useSesionPortal } from "./sesionPortal";

/** Entrada al portal: iniciar sesión o estrenar el acceso con el código del mostrador. */
export default function PortalEntrada() {
  const { entrar } = useSesionPortal();
  const { t, aplicarPorDefecto, delFallo } = useIdioma();
  const [modo, setModo] = useState<"entrar" | "activar">("entrar");
  const [club, setClub] = useState(localStorage.getItem(CLAVE_CLUB) ?? "");
  const [nombreClub, setNombreClub] = useState<string | null>(null);
  const [numeroSocio, setNumeroSocio] = useState("");
  const [password, setPassword] = useState("");
  const [codigo, setCodigo] = useState("");
  const [repetir, setRepetir] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [enviando, setEnviando] = useState(false);

  /** Si el club venía en la dirección, no hay que preguntarlo otra vez. */
  const [clubDeLaUrl, setClubDeLaUrl] = useState(false);

  // El club se toma de la dirección (/portal/mi-club) o de la última vez.
  useEffect(() => {
    const desdeUrl = window.location.pathname
      .replace(/^\/portal\/?/, "")
      .split("/")[0];
    if (desdeUrl) {
      setClub(desdeUrl);
      setClubDeLaUrl(true);
    }
  }, []);

  useEffect(() => {
    if (!club.trim()) {
      setNombreClub(null);
      return;
    }
    const temporizador = setTimeout(async () => {
      try {
        const datos = await api<{ nombre: string; idioma?: Idioma }>(
          `/portal/club/${encodeURIComponent(club.trim())}`,
        );
        setNombreClub(datos.nombre);
        // El club ya dice en qué idioma habla, antes de entrar.
        aplicarPorDefecto(datos.idioma);
      } catch {
        setNombreClub(null);
      }
    }, 300);
    return () => clearTimeout(temporizador);
  }, [club, aplicarPorDefecto]);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setOk("");
    setEnviando(true);
    try {
      if (modo === "entrar") {
        await entrar(club.trim(), Number(numeroSocio), password);
      } else {
        if (password !== repetir) throw new Error(t("entrada.noCoinciden"));
        await api("/portal/activar", {
          metodo: "POST",
          cuerpo: {
            club: club.trim(),
            numeroSocio: Number(numeroSocio),
            codigo,
            password,
          },
        });
        setOk(t("entrada.accesoCreado"));
        setModo("entrar");
        setCodigo("");
        setRepetir("");
      }
    } catch (fallo) {
      setError(delFallo(fallo, "general.fallo"));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="portal-entrada">
      <form className="portal-caja" onSubmit={enviar}>
        <div className="centrado" style={{ marginBottom: 18 }}>
          <div
            className="logo-circulo"
            style={{
              margin: "0 auto 10px",
              width: 46,
              height: 46,
              fontSize: "1.2rem",
            }}
          >
            {nombreClub?.charAt(0) ?? "·"}
          </div>
          <h1 style={{ fontSize: "1.2rem" }}>
            {nombreClub ?? t("entrada.titulo")}
          </h1>
          <p className="suave pequeno" style={{ margin: "4px 0 0" }}>
            {modo === "entrar"
              ? t("entrada.subtituloEntrar")
              : t("entrada.subtituloActivar")}
          </p>
        </div>

        <Aviso tipo="error">{error}</Aviso>
        <Aviso tipo="ok">{ok}</Aviso>

        {/*
         * Si el club viene en la dirección y existe, su nombre ya está arriba:
         * volver a pedir el identificador es una casilla de más en un móvil,
         * y encima invita a tocar lo que ya estaba bien. Solo se enseña
         * cuando hay algo que corregir.
         */}
        {!(clubDeLaUrl && nombreClub) && (
          <Campo etiqueta={t("entrada.club")} ayuda={t("entrada.clubAyuda")}>
            <input
              value={club}
              onChange={(e) => setClub(e.target.value)}
              required
              placeholder="mi-club"
            />
          </Campo>
        )}

        <Campo etiqueta={t("entrada.numeroSocio")}>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            value={numeroSocio}
            onChange={(e) => setNumeroSocio(e.target.value)}
            required
          />
        </Campo>

        {modo === "activar" && (
          <Campo
            etiqueta={t("entrada.codigo")}
            ayuda={t("entrada.codigoAyuda")}
          >
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              required
            />
          </Campo>
        )}

        <Campo
          etiqueta={
            modo === "entrar"
              ? t("entrada.contrasena")
              : t("entrada.eligeContrasena")
          }
          ayuda={modo === "activar" ? t("entrada.minimo8") : undefined}
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={
              modo === "entrar" ? "current-password" : "new-password"
            }
            required
          />
        </Campo>

        {modo === "activar" && (
          <Campo etiqueta={t("entrada.repiteContrasena")}>
            <input
              type="password"
              value={repetir}
              onChange={(e) => setRepetir(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Campo>
        )}

        <button
          className="boton grande bloque"
          disabled={enviando}
          style={{ marginTop: 6 }}
        >
          {enviando
            ? t("general.unMomento")
            : modo === "entrar"
              ? t("general.entrar")
              : t("entrada.crearAcceso")}
        </button>

        <p className="centrado pequeno suave" style={{ marginBottom: 0 }}>
          {modo === "entrar" ? (
            <>
              {t("entrada.primeraVez")}{" "}
              <button
                type="button"
                className="boton fantasma"
                onClick={() => setModo("activar")}
              >
                {t("entrada.activarAcceso")}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="boton fantasma"
              onClick={() => setModo("entrar")}
            >
              {t("entrada.yaTengoContrasena")}
            </button>
          )}
        </p>

        <div className="centrado" style={{ marginTop: 14 }}>
          <SelectorIdioma />
        </div>
      </form>
    </div>
  );
}
